/**
 * Microsoft Clarity Analytics Hook
 * 
 * Sets custom tags to segment session recordings and heatmaps.
 * Tags allow filtering in Clarity dashboard by user type, page, etc.
 */

import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Helper to safely call Clarity
const clarityTag = (key, value) => {
  if (typeof window !== 'undefined' && window.clarity) {
    try {
      window.clarity('set', key, value);
    } catch (e) {
      // Silently fail if Clarity not loaded
    }
  }
};

// Identify user in Clarity (for session filtering)
const clarityIdentify = (userId, sessionId, customPage) => {
  if (typeof window !== 'undefined' && window.clarity) {
    try {
      window.clarity('identify', userId, sessionId, customPage);
    } catch (e) {
      // Silently fail
    }
  }
};

// Get page type from pathname
const getPageType = (pathname) => {
  if (pathname === '/' || pathname === '/home') return 'home';
  if (pathname.startsWith('/passeio/')) return 'trip_view';
  if (pathname.startsWith('/grupo/')) return 'group_view';
  if (pathname.startsWith('/motard/')) return 'profile_view';
  if (pathname.startsWith('/meus-passeios')) return 'my_trips';
  if (pathname.startsWith('/meus-grupos')) return 'my_groups';
  if (pathname.startsWith('/garagem')) return 'garage';
  if (pathname.startsWith('/definicoes')) return 'settings';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/login') || pathname.startsWith('/register')) return 'auth';
  if (pathname.startsWith('/explorar')) return 'explore';
  if (pathname.startsWith('/about') || pathname.startsWith('/apresentacao') || pathname.startsWith('/faq') || pathname.startsWith('/ajuda') || pathname.startsWith('/privacy') || pathname.startsWith('/contact')) return 'static';
  return 'other';
};

export const useClarity = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Set user-related tags when auth state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      // User type
      const userType = user.role === 'admin' ? 'admin' : user.role === 'owner' ? 'owner' : 'member';
      clarityTag('user_type', userType);
      clarityTag('user_role', user.role || 'member');
      
      // Identify user for session linking (using anonymous ID)
      // Don't send email/PII - just user_id hash
      const anonymousId = user.user_id ? user.user_id.substring(0, 8) : 'unknown';
      clarityIdentify(anonymousId);
      
    } else {
      clarityTag('user_type', 'anonymous');
      clarityTag('user_role', 'visitor');
    }
  }, [isAuthenticated, user]);

  // Set page type when route changes
  useEffect(() => {
    const pageType = getPageType(location.pathname);
    clarityTag('page_type', pageType);
    
    // Track specific content IDs for analysis
    if (location.pathname.startsWith('/passeio/')) {
      const tripId = location.pathname.split('/')[2];
      if (tripId) clarityTag('content_id', tripId);
    } else if (location.pathname.startsWith('/clube/')) {
      const clubId = location.pathname.split('/')[2];
      if (clubId) clarityTag('content_id', clubId);
    }
  }, [location.pathname]);

  // Set language tag
  useEffect(() => {
    clarityTag('language', i18n.language || 'pt');
  }, [i18n.language]);

  // Return helper for custom event tracking
  return {
    // Track custom events (shows in Clarity as tags)
    trackEvent: (eventName, eventData = {}) => {
      clarityTag('event', eventName);
      Object.entries(eventData).forEach(([key, value]) => {
        clarityTag(`event_${key}`, String(value));
      });
    },
    
    // Set custom tag
    setTag: clarityTag,
  };
};

export default useClarity;
